/**
 * MarketingRender — isolated full-bleed canvas for recording paid-social
 * animated ads. Rendered OUTSIDE the main AppWrapper (no Navbar, Footer,
 * SiteGate, Toasters, ChatWidget, etc.) so headless Playwright captures
 * only the ad creative.
 *
 * URL shape:  /render-marketing/:variant/:aspect
 *   :variant  = science | aurora | datagrid | spotlight | neon | workout
 *   :aspect   = square (1080x1080) | portrait (1080x1920)
 *
 * The route is intentionally NOT linked from anywhere in the UI — it's
 * a render harness, not a public page.
 */
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import ScienceVariant from "@/components/marketing/ScienceVariant";
import AuroraVariant from "@/components/marketing/AuroraVariant";
import DataGridVariant from "@/components/marketing/DataGridVariant";
import SpotlightVariant from "@/components/marketing/SpotlightVariant";
import NeonVariant from "@/components/marketing/NeonVariant";
import WorkoutVariant from "@/components/marketing/WorkoutVariant";

const VARIANTS = {
  science: ScienceVariant,
  aurora: AuroraVariant,
  datagrid: DataGridVariant,
  spotlight: SpotlightVariant,
  neon: NeonVariant,
  workout: WorkoutVariant,
};

export default function MarketingRender() {
  const { variant = "science", aspect = "square" } = useParams();
  const Variant = VARIANTS[variant] || ScienceVariant;
  const isPortrait = aspect === "portrait";
  const width = isPortrait ? 1080 : 1080;
  const height = isPortrait ? 1920 : 1080;

  useEffect(() => {
    // Strip body/html margins so the canvas is edge-to-edge — Playwright
    // viewport is set to the exact target resolution.
    document.documentElement.style.margin = "0";
    document.documentElement.style.padding = "0";
    document.body.style.margin = "0";
    document.body.style.padding = "0";
    document.body.style.background = "#000";
    document.body.style.overflow = "hidden";
  }, []);

  return (
    <div
      data-testid="marketing-render-root"
      data-variant={variant}
      data-aspect={aspect}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        position: "relative",
        overflow: "hidden",
        background: "#000",
        fontFamily: "'Outfit', 'Helvetica Neue', Helvetica, Arial, sans-serif",
      }}
    >
      <Variant portrait={isPortrait} />
    </div>
  );
}
